import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIME_SLOTS = [
  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '13:00', '13:30', '14:00', '14:30', '15:00', '15:30',
  '16:00', '16:30', '17:00', '17:30'
];

const EMPTY_BOOKING = { name: '', email: '', phone: '', service: '', date: '', time: '' };

const BookingModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [bookingData, setBookingData] = useState(EMPTY_BOOKING);
  const [bookingSubmitting, setBookingSubmitting] = useState(false);
  const [bookingStatus, setBookingStatus] = useState(null);

  const bookingServices = [
    { value: 'houses', label: t('booking.services.houses') },
    { value: 'insurance', label: t('booking.services.insurance') },
    { value: 'renovations', label: t('booking.services.renovations') },
    { value: 'cars', label: t('booking.services.cars') },
    { value: 'towing', label: t('booking.services.towing') },
    { value: 'financing', label: t('booking.services.financing') }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBookingData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBookingSubmitting(true);
    setBookingStatus(null);

    try {
      await axios.post(`${API}/booking`, bookingData);
      setBookingStatus({ type: 'success', message: t('booking.success') });
      setBookingData(EMPTY_BOOKING);
      setTimeout(() => {
        onClose();
        setBookingStatus(null);
      }, 3000);
    } catch (error) {
      setBookingStatus({
        type: 'error',
        message: error.response?.data?.detail || t('booking.error')
      });
    } finally {
      setBookingSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="home-booking-modal"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold">{t('booking.title')}</h2>
                <p className="text-green-100 text-sm">{t('booking.subtitle')}</p>
              </div>
            </div>
            <button onClick={onClose} className="text-white hover:text-gray-200">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label htmlFor="booking-name" className="block text-sm font-semibold text-gray-700 mb-2">{t('booking.name')} *</label>
            <input type="text" id="booking-name" name="name" value={bookingData.name} onChange={handleInputChange} required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition"
              disabled={bookingSubmitting} />
          </div>
          <div>
            <label htmlFor="booking-email" className="block text-sm font-semibold text-gray-700 mb-2">{t('booking.email')} *</label>
            <input type="email" id="booking-email" name="email" value={bookingData.email} onChange={handleInputChange} required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition"
              disabled={bookingSubmitting} />
          </div>
          <div>
            <label htmlFor="booking-phone" className="block text-sm font-semibold text-gray-700 mb-2">{t('booking.phone')} *</label>
            <input type="tel" id="booking-phone" name="phone" value={bookingData.phone} onChange={handleInputChange} required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition"
              disabled={bookingSubmitting} />
          </div>
          <div>
            <label htmlFor="booking-service" className="block text-sm font-semibold text-gray-700 mb-2">{t('booking.service')} *</label>
            <select id="booking-service" name="service" value={bookingData.service} onChange={handleInputChange} required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition bg-white"
              disabled={bookingSubmitting}>
              <option value="">{t('booking.selectService')}</option>
              {bookingServices.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="booking-date" className="block text-sm font-semibold text-gray-700 mb-2">{t('booking.date')} *</label>
              <input type="date" id="booking-date" name="date" value={bookingData.date} onChange={handleInputChange} required
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition"
                disabled={bookingSubmitting} />
            </div>
            <div>
              <label htmlFor="booking-time" className="block text-sm font-semibold text-gray-700 mb-2">{t('booking.time')} *</label>
              <select id="booking-time" name="time" value={bookingData.time} onChange={handleInputChange} required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition bg-white"
                disabled={bookingSubmitting}>
                <option value="">{t('booking.selectTime')}</option>
                {TIME_SLOTS.map((time) => <option key={time} value={time}>{time}</option>)}
              </select>
            </div>
          </div>

          {bookingStatus && (
            <div className={`p-4 rounded-lg ${bookingStatus.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              <div className="flex items-center gap-2">
                {bookingStatus.type === 'success' ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                )}
                {bookingStatus.message}
              </div>
            </div>
          )}

          <button type="submit" disabled={bookingSubmitting}
            className="w-full bg-green-600 text-white px-6 py-4 rounded-lg text-lg font-semibold hover:bg-green-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed transform hover:scale-105 active:scale-95 flex items-center justify-center gap-2">
            {bookingSubmitting ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('booking.submitting')}
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                {t('booking.submit')}
              </>
            )}
          </button>
          <p className="text-xs text-gray-500 text-center">* {t('booking.hours')}</p>
        </form>
      </div>
    </div>
  );
};

export default BookingModal;
